import React, { useEffect, useRef, useState } from "react";
import { useAsyncEffect } from "tap/utils/hooks/useAsyncEffect";
import { useLoading } from "tap/utils/hooks/useLoading";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Input from "@dtci/spellbook/Input";
import Button from "@dtci/spellbook/Button";
import { Col, Row } from "antd";
import { MultiSelect } from "@dtci/spellbook/MultiSelect";
import { InputLabel } from "tap/components/InputLabel";
import "./styles.scss";
import { tapApi } from "tap/api.js";
import { parseSearchParams, updateSearchParams } from "tap/utils/urlParams.js";
import { getItemRange } from "tap/utils/pagination.js";
import {
  cleanUserFilter,
  buildFilterFromParams,
} from "./utils/usersSearchUtils.js";
import {
  validateSpecialCharacters,
  validateEmail,
} from "tap/utils/validateSpecialCharacters.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useUsersActions } from "tap/store/users/useUsers.js";
import { useUsersData } from "../../hooks/useUsersData";

/**
 * Search form for filtering the Users grid.
 *
 * Available filters:
 * - Name: text search on user's name
 * - Email: text search on user's email
 * - Roles: multi-select dropdown for user roles
 *
 * Filter state syncs bidirectionally with URL params for shareable searches.
 * Changes trigger debounced searches (400ms delay).
 */
export function UsersSearchForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const isInitialMount = useRef(true);
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const {
    fetchUsers,
    updateUsers,
    userRange,
    filterForm,
    userSort,
    itemsPerPage,
  } = useUsersData();
  const { updateUsersSearchForm, clearUsers } = useUsersActions();
  const { roleOptions } = useSelector((s) => s.users);
  const { showErrorToast } = useToasts();

  const [validationErrors, setValidationErrors] = useState({
    name: undefined,
    email: undefined,
  });
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useAsyncEffect(async ({ isMounted }) => {
    if (!Array.isArray(roleOptions) || roleOptions.length === 0) {
      try {
        const roles = await tapApi.userRoles();
        if (isMounted()) {
          updateUsers({ roleOptions: Array.isArray(roles) ? roles : [] });
        }
      } catch (error) {
        if (isMounted()) {
          showErrorToast("Failed to load role options");
        }
      }
    }
  }, []);

  // Sync URL params to form state on navigation (location.key changes on each navigation)
  useEffect(() => {
    const params = parseSearchParams(location.search);
    const filterData = buildFilterFromParams(params, roleOptions);

    let rangeOverride;
    if (filterData.pageSize) {
      rangeOverride = getItemRange(0, filterData.pageSize);
      updateUsers({ itemsPerPage: filterData.pageSize });
    }

    const hasUrlParams = Object.keys(filterData).length > 0;

    if (hasUrlParams) {
      updateUsersSearchForm({ key: "filterForm", data: filterData });
    }

    // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
    // only carries partial params (e.g. pageSize).
    const filterForFetch =
      hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

    fetchData(filterForFetch, rangeOverride);
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Re-parse URL params when roleOptions load (needed for multi-select values that depend on options)
  useEffect(() => {
    if (
      isInitialMount.current ||
      !Array.isArray(roleOptions) ||
      roleOptions.length === 0
    )
      return;

    const params = parseSearchParams(location.search);
    if (params.role) {
      const filterData = buildFilterFromParams(params, roleOptions);
      const mergedFilterData = { ...filterForm, ...filterData };
      updateUsersSearchForm({ key: "filterForm", data: filterData });
      fetchData(mergedFilterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleOptions]);

  const fetchData = async (filterData, rangeOverride) => {
    setLoadingTrue();
    const range = rangeOverride ?? getItemRange(0, itemsPerPage);
    const data = await fetchUsers(range, filterData);
    if (data) {
      updateUsers({
        users: data?.content ?? [],
        totalPages: data?.totalPages ?? 0,
        currentPage: data?.pageable?.pageNumber ?? 0,
        totalItems: data?.totalElements ?? 0,
        userRange: range,
      });
    }
    setLoadingFalse();
  };

  // Reset navigates to clean URL; the location.key useEffect handles re-fetch
  const handleResetFilter = () => {
    clearUsers();
    setValidationErrors({
      name: undefined,
      email: undefined,
    });
    navigate(location.pathname);
  };

  function handleInputChange(field, value) {
    let newValidationErrors = validationErrors;
    if (field === "name") {
      const error = validateSpecialCharacters(value);
      newValidationErrors = { ...validationErrors, [field]: error };
      setValidationErrors(newValidationErrors);
    } else if (field === "email") {
      const error = validateEmail(value);
      newValidationErrors = { ...validationErrors, [field]: error };
      setValidationErrors(newValidationErrors);
    }

    const newFilterForm = { ...filterForm, [field]: value };
    updateUsersSearchForm({
      key: "filterForm",
      data: { [field]: value },
    });

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const hasErrors = Object.values(newValidationErrors).some(
        (error) => error,
      );
      if (!hasErrors) {
        const filtered = cleanUserFilter(
          newFilterForm,
          getItemRange(0, itemsPerPage),
          userSort,
        );
        updateSearchParams(location, navigate, filtered);
      }
    }, 400);
  }

  return (
    <Row className="users-filter-form">
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Name">
          <Input
            value={filterForm.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            width="auto"
            id="name"
            name="name"
            placeholder="Enter"
            className={validationErrors.name ? "input-error" : ""}
          />
          {validationErrors.name && (
            <div className="validation-error">{validationErrors.name}</div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Email">
          <Input
            value={filterForm.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            width="auto"
            id="email"
            name="email"
            placeholder="Enter"
            className={validationErrors.email ? "input-error" : ""}
          />
          {validationErrors.email && (
            <div className="validation-error">{validationErrors.email}</div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Roles">
          <MultiSelect
            options={roleOptions}
            isClearable
            isSearchable
            onChange={(value) => handleInputChange("role", value)}
            value={filterForm.role || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <Button
          className="reset-button"
          sizeType="small"
          styleType="standard"
          onClick={handleResetFilter}
        >
          Reset
        </Button>
      </Col>
    </Row>
  );
}
